/*
 * Lemonade Training Oy copy rights all right reserved.
 * Terms Acceptance Type Definitions (F019 — gym terms, Lane 2)
 *
 * Immutable gym-terms acceptance evidence — one record per commitment line,
 * written pre-payment in the same batch as ConsentEvents — plus the checkout
 * wire shapes around it: the request payload and the self-sufficient refusal
 * details. Documents are never client-writable (rules: read record-owner
 * only, write false).
 */

import { Timestamp } from "firebase/firestore";
import { GymTermsBoxOwnerKey, GymTermsLocale } from "./gymTerms";

/**
 * Immutable record at TermsAcceptanceEvents/{eventID}: the buyer accepted
 * this gym's terms at this version/hash before payment. Deliberately carries
 * no subscriptionId — the record is complete and final the moment it lands.
 */
export interface TermsAcceptanceEvent {
  userUID: string;
  boxID: string;
  /** Dereferences the archived version at PublishedTerms/{ownerKey} directly; pinned to box — coach terms would be their own record kind. */
  ownerKey: GymTermsBoxOwnerKey;
  termsVersion: string;
  /** Server-re-derived from stored bytes, never the client's value. */
  termsContentHash: string;
  /** Version/hash SHOWN to the buyer, resolved at acceptance time from the live copy module — not the pointer's publish-time snapshot, which can lag. */
  noticeVersion: string;
  noticeContentHash: string;
  locale: GymTermsLocale;
  productUID: string;
  priceID: string;
  acceptedAt: Timestamp | Date;
  /** Literal true: acceptance was captured BEFORE payment. */
  capturedPrePayment: true;
  /** GDPR minimization: sha256(uid|ip) hash, or omitted — never the raw IP. */
  ipHash?: string;
  /** Free client text, server-capped at 512 chars. */
  userAgent?: string;
}

/** The `gymTermsConsent` request field on createStripeCheckoutSession — a sibling of commitmentConsent, never a widening of it. */
export interface GymTermsConsentPayload {
  boxID: string;
  termsVersion: string;
  termsContentHash: string;
}

// Runtime array is the single source of truth — the union derives from it.
export const GYM_TERMS_REFUSAL_REASONS = [
  "terms_acceptance_missing",
  "terms_stale",
  "terms_slug_ambiguous",
] as const;
export type GymTermsRefusalReason = (typeof GYM_TERMS_REFUSAL_REASONS)[number];

/**
 * Refusal wire shape carried in the gate's HttpsError details. Self-sufficient
 * by design: the checkout renders the terms checkbox and link from this
 * payload alone, no second summary call. Version/hash are the server's
 * CURRENT pointer values — derived from GymTermsConsentPayload so the shape
 * the client echoes back is compiler-bound to the shape it was refused with.
 */
export type GymTermsRefusalDetails = GymTermsConsentPayload & {
  reason: GymTermsRefusalReason;
  publicUrl: string;
};
